// src/runtime/ActivityBus.ts
// ============================================
// XEAOS 活动事件总线（v6.3.24）
//
// 职责：把系统内真实发生的事件（Runtime 就绪 / 工作流执行 / 内容发布等）统一汇总，
//       localStorage 持久化（刷新页面不丢失），供 Live Company 实时流等消费。
//
// 铁律：只允许写入真实事件，禁止虚构/伪造事件。Workflow 执行事件由 useCockpitData
//       从 WorkflowRuntime 拉取后持久化；系统事件由 RuntimeManager 初始化完成时写入。
// ============================================

export type StreamEventType =
  | "system.ready"
  | "workflow.started"
  | "workflow.completed"
  | "workflow.failed"
  | "content.generated"
  | "content.published"
  | "knowledge.recorded";

export type StreamEventStatus = "running" | "success" | "failed" | "warning" | "info";

export interface StreamEvent {
  id: string;
  type: StreamEventType;
  status: StreamEventStatus;
  summary: string;
  /** ISO 时间字符串 */
  timestamp: string;
  /** 来源（Runtime 名 / System 等） */
  source?: string;
}

const STORAGE_KEY = "xeaos_activity_stream";
const MAX_EVENTS = 100;

class ActivityBus {
  private events: StreamEvent[] | null = null;

  private load(): StreamEvent[] {
    if (this.events) return this.events;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      this.events = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.events = [];
    }
    return this.events;
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.events));
    } catch {
      // localStorage 不可用时静默（降级为内存态）
    }
  }

  /** 记录一条真实事件（新事件置顶） */
  push(
    type: StreamEventType,
    status: StreamEventStatus,
    summary: string,
    source?: string
  ): StreamEvent {
    const event: StreamEvent = {
      id: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type,
      status,
      summary,
      timestamp: new Date().toISOString(),
      source,
    };
    const list = this.load();
    list.unshift(event);
    if (list.length > MAX_EVENTS) list.length = MAX_EVENTS;
    this.save();
    return event;
  }

  /** 幂等记录：相同 summary 只记一条（用于"系统就绪"这类重复事件，避免每次刷新刷屏） */
  pushOnce(
    type: StreamEventType,
    status: StreamEventStatus,
    summary: string,
    source?: string
  ): StreamEvent | null {
    const list = this.load();
    if (list.some((e) => e.summary === summary)) return null;
    return this.push(type, status, summary, source);
  }

  /** 列出最近事件（新的在前） */
  list(limit = 50): StreamEvent[] {
    return this.load().slice(0, limit);
  }

  clear(): void {
    this.events = [];
    this.save();
  }
}

export const activityBus = new ActivityBus();
export default activityBus;
